import type {
  DailyAttendanceRecord,
  LateExcuse,
  AttendancePunchLog,
  AttendanceCorrection,
  PaginatedResponse,
  DataResponse,
} from '@pms/shared';
import { api } from '@/lib/api';

export interface AttendanceDailyQuery {
  page?: number;
  limit?: number;
  /** Return every matching row in one response (server skips LIMIT/OFFSET) */
  all?: boolean;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
  regularization_status?: string;
  search?: string;
}

export interface DailyRecordWithUser extends DailyAttendanceRecord {
  first_name: string | null;
  last_name: string | null;
  employee_code: string | null;
  photo_url: string | null;
  penalty_approved_minutes: number;
}

export interface PunchLogWithUser extends AttendancePunchLog {
  first_name: string | null;
  last_name: string | null;
  employee_code: string | null;
  photo_url: string | null;
}

/** One work or break stretch between two punches, measured server-side. */
export interface PunchSegment {
  kind: 'work' | 'break';
  from_punch_id: string;
  to_punch_id: string | null;
  start: string;
  end: string;
  minutes: number;
}

export interface PunchTimelineTotals {
  working_minutes: number;
  break_minutes: number;
  grace_minutes: number;
  effective_working_minutes: number;
  check_ins: number;
  check_outs: number;
  has_unbalanced_punches: boolean;
  /** True while the day's last punch is an unclosed check-in on today's date. */
  open_check_in: boolean;
  /**
   * When that open check-in happened. The client ticks from here so an in-progress
   * stretch reads live rather than frozen at the last request. It is not part of
   * `working_minutes` — an open stretch stays unpaid until the punch-out closes it.
   */
  running_since: string | null;
}

export interface PunchTimeline {
  punches: PunchLogWithUser[];
  segments: PunchSegment[];
  totals: PunchTimelineTotals;
}

export interface PunchLogsQuery {
  page?: number;
  limit?: number;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  punch_type?: 'check_in' | 'check_out';
}

export interface TodayStatus {
  punches: AttendancePunchLog[];
  record: DailyAttendanceRecord | null;
}

export interface AttendanceChangeLog {
  id: string;
  action_type: string;
  target_user_id: string;
  performed_by_id: string;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  remarks: string | null;
  related_punch_id: string | null;
  related_record_id: string | null;
  created_at: string;
  // joined
  target_first_name: string | null;
  target_last_name: string | null;
  target_employee_code: string | null;
  target_photo_url: string | null;
  performed_first_name: string | null;
  performed_last_name: string | null;
}

export interface ChangeLogsQuery {
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  user_id?: string;
  performer_id?: string;
  action_type?: string;
  search?: string;
}

export interface UnavailableUser {
  user_id: string;
  first_name: string;
  last_name: string;
  employee_code: string;
  photo_url: string | null;
  // leaves only
  leave_type_name?: string;
  is_half_day?: boolean;
  half_day_period?: string | null;
  leave_end_date?: string;
  leave_kind?: 'full' | 'half' | 'short';
  // short leaves only — TIME strings "HH:MM:SS" (IST wall clock)
  from_time?: string;
  to_time?: string;
  duration_minutes?: number;
  short_windows?: Array<{ from_time: string; to_time: string; duration_minutes: number }>;
  // clocked_out_not_in only
  last_punch_out?: string;
}

export interface UnavailableToday {
  total: number;
  leaves: UnavailableUser[];
  not_clocked_in: UnavailableUser[];
  clocked_out_not_in: UnavailableUser[];
}

export const attendanceService = {
  /** Admin: all daily records (paginated + filterable) */
  async listDaily(
    query: AttendanceDailyQuery = {},
  ): Promise<PaginatedResponse<DailyRecordWithUser>> {
    const res = await api.get<PaginatedResponse<DailyRecordWithUser>>(
      '/attendance/daily',
      { params: query },
    );
    return res.data;
  },

  /**
   * Late days in a range that approved leave excuses, decided by the backend
   * penalty rule. The calendar shows these as present instead of late.
   */
  async listLateExcuses(params: {
    user_id: string;
    date_from: string;
    date_to: string;
  }): Promise<DataResponse<LateExcuse[]>> {
    const res = await api.get<DataResponse<LateExcuse[]>>(
      '/attendance/late-excuses',
      { params },
    );
    return res.data;
  },

  /** Own daily history */
  async listMyAttendance(
    query: AttendanceDailyQuery = {},
  ): Promise<PaginatedResponse<DailyAttendanceRecord>> {
    const res = await api.get<PaginatedResponse<DailyAttendanceRecord>>(
      '/attendance/daily/me',
      { params: query },
    );
    return res.data;
  },

  /** Today status (own) */
  async getTodayStatus(): Promise<TodayStatus> {
    const res = await api.get<TodayStatus>('/attendance/today');
    return res.data;
  },

  /** Today status for a specific user (admin) */
  async getTodayStatusForUser(userId: string): Promise<TodayStatus> {
    const res = await api.get<TodayStatus>(`/attendance/today/${userId}`);
    return res.data;
  },

  /** Admin: paginated punch logs across all users */
  async listPunchLogs(
    query: PunchLogsQuery = {},
  ): Promise<PaginatedResponse<PunchLogWithUser>> {
    const res = await api.get<PaginatedResponse<PunchLogWithUser>>(
      '/attendance/punches',
      { params: query },
    );
    return res.data;
  },

  /**
   * Punch timeline for one user on one date: every punch of the IST day (no page limit),
   * the work/break stretches between them, and the day's totals. The totals are the same
   * daily_attendance_records row the attendance report list renders, so the timeline modal
   * and the report can never show different working hours.
   */
  async getPunchTimeline(userId: string, date: string): Promise<PunchTimeline> {
    const res = await api.get<PunchTimeline>('/attendance/timeline', {
      params: { user_id: userId, date },
    });
    return res.data;
  },

  /** Punch in/out */
  async punch(input: {
    punch_type: 'check_in' | 'check_out';
    punch_time?: string;
    punch_source?: string;
    work_mode?: string;
  }): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(
      '/attendance/punch',
      input,
    );
    return res.data.data;
  },

  /** Admin: edit an existing punch log (time, type, work mode) */
  async updatePunch(
    punchLogId: string,
    input: {
      punch_type?: 'check_in' | 'check_out';
      punch_time?: string;
      work_mode?:  string;
      metadata?:   Record<string, unknown>;
    },
  ): Promise<{ id: string }> {
    const res = await api.patch<DataResponse<{ id: string }>>(
      `/attendance/punches/${punchLogId}`,
      input,
    );
    return res.data.data;
  },

  /** Admin: soft-delete a punch log (requires reason for audit trail) */
  async deletePunch(punchLogId: string, reason: string): Promise<void> {
    await api.delete(`/attendance/punches/${punchLogId}`, { data: { reason } });
  },

  /** Employee: request a regularization correction for a daily record */
  async requestRegularization(recordId: string, reason: string): Promise<void> {
    await api.post(`/attendance/${recordId}/request-regularization`, { reason });
  },

  /** Admin: approve or reject a regularization request */
  async approveRegularization(
    recordId: string,
    status: 'approved' | 'rejected',
    reason: string,
  ): Promise<void> {
    await api.patch(`/attendance/${recordId}/regularize`, { status, reason });
  },

  /** Admin: manually add a punch for any user (calls kiosk-punch endpoint) */
  async addAdminPunch(input: {
    user_id:    string;
    punch_type: 'check_in' | 'check_out';
    punch_time: string;
    work_mode?: string;
    metadata?:  Record<string, unknown>;
  }): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(
      '/attendance/kiosk-punch',
      input,
    );
    return res.data.data;
  },

  /** Admin: paginated attendance change/audit logs */
  async listLogs(
    query: ChangeLogsQuery = {},
  ): Promise<PaginatedResponse<AttendanceChangeLog>> {
    const res = await api.get<PaginatedResponse<AttendanceChangeLog>>(
      '/attendance/logs',
      { params: query },
    );
    return res.data;
  },

  /** Employee: own punch logs for a date range */
  async getMyPunchLogs(query: {
    date_from: string;
    date_to:   string;
    punch_type?: 'check_in' | 'check_out';
  }): Promise<AttendancePunchLog[]> {
    const res = await api.get<{ data: AttendancePunchLog[] }>(
      '/attendance/my-punches',
      { params: query },
    );
    return res.data.data;
  },

  /** Employee: submit a correction request for a daily record */
  async createCorrection(
    recordId: string,
    reason: string,
    punchEdit?:
      | { punch_log_id: string; requested_punch_time: string }
      | { requested_punch_type: 'check_in' | 'check_out'; requested_punch_time: string },
  ): Promise<AttendanceCorrection> {
    const res = await api.post<DataResponse<AttendanceCorrection>>(
      `/attendance/${recordId}/corrections`,
      { reason, ...punchEdit },
    );
    return res.data.data;
  },

  /** Employee: own correction history (paginated) */
  async listMyCorrections(query: {
    page?:            number;
    limit?:           number;
    status?:          'requested' | 'approved' | 'rejected';
    daily_record_id?: string;
  } = {}): Promise<PaginatedResponse<AttendanceCorrection>> {
    const res = await api.get<PaginatedResponse<AttendanceCorrection>>(
      '/attendance/my-corrections',
      { params: query },
    );
    return res.data;
  },

  /** Admin: all correction requests (paginated + filterable) */
  async listAllCorrections(query: {
    page?:            number;
    limit?:           number;
    status?:          'requested' | 'approved' | 'rejected';
    user_id?:         string;
    daily_record_id?: string;
    /** IST request date range (YYYY-MM-DD, inclusive) */
    date_from?:       string;
    date_to?:         string;
    /** Employee name / code search (server-side) */
    q?:               string;
  } = {}): Promise<PaginatedResponse<AttendanceCorrection>> {
    const res = await api.get<PaginatedResponse<AttendanceCorrection>>(
      '/attendance/corrections',
      { params: query },
    );
    return res.data;
  },

  /** Admin: company-wide attendance summary for a single date (no pagination) */
  async getDailySummary(date: string, userId?: string): Promise<{
    present: number;
    late: number;
    half_day: number;
    absent: number;
    total_swipes: number;
    avg_hours: number;
  }> {
    const res = await api.get<{ data: { present: number; late: number; half_day: number; absent: number; total_swipes: number; avg_hours: number } }>(
      '/attendance/daily/summary',
      { params: { date, ...(userId ? { user_id: userId } : {}) } },
    );
    return res.data.data;
  },

  /** Admin: all unavailable employees for today grouped by reason */
  async getUnavailableToday(): Promise<UnavailableToday> {
    const res = await api.get<{ data: UnavailableToday }>('/attendance/unavailable-today');
    return res.data.data;
  },

  /** Admin: approve or reject a specific correction */
  async reviewCorrection(
    correctionId:  string,
    status:        'approved' | 'rejected',
    adminRemarks?: string,
  ): Promise<void> {
    await api.patch(`/attendance/corrections/${correctionId}/review`, {
      status,
      admin_remarks: adminRemarks,
    });
  },

  async revertCorrection(correctionId: string): Promise<void> {
    await api.patch(`/attendance/corrections/${correctionId}/revert`);
  },

  /** Admin: punch deletion aggregate stats (by performer + by role) */
  async getPunchDeletionStats(): Promise<{
    total: number;
    by_performer: Array<{
      performed_by_id: string; first_name: string | null; last_name: string | null;
      employee_code: string | null; photo_url: string | null;
      delete_count: number; last_deleted_at: string;
    }>;
    by_role: Array<{ role_name: string; role_code: string; delete_count: number }>;
  }> {
    const res = await api.get('/attendance/deletion-stats');
    return res.data.data;
  },

  /** Admin: full punch-delete permission config */
  async getPunchDeleteConfig(): Promise<{
    mode: 'disabled' | 'role_wise' | 'person_wise';
    allowed_roles: string[];
    person_grants: Array<{
      id: string;
      granter_user_id: string; granter_first_name: string | null; granter_last_name: string | null; granter_employee_code: string | null;
      target_user_id: string;  target_first_name: string | null;  target_last_name: string | null;  target_employee_code: string | null;
    }>;
  }> {
    const res = await api.get('/attendance/punch-delete-config');
    return res.data.data;
  },

  async updatePunchDeleteMode(mode: string): Promise<void> {
    await api.put('/attendance/punch-delete-config/mode', { mode });
  },

  async addPunchDeleteRoleGrant(roleCode: string): Promise<void> {
    await api.post('/attendance/punch-delete-config/role-grants', { role_code: roleCode });
  },

  async removePunchDeleteRoleGrant(roleCode: string): Promise<void> {
    await api.delete(`/attendance/punch-delete-config/role-grants/${roleCode}`);
  },

  async addPunchDeletePersonGrant(granterUserId: string, targetUserId: string): Promise<void> {
    await api.post('/attendance/punch-delete-config/person-grants', {
      granter_user_id: granterUserId,
      target_user_id:  targetUserId,
    });
  },

  async removePunchDeletePersonGrant(grantId: string): Promise<void> {
    await api.delete(`/attendance/punch-delete-config/person-grants/${grantId}`);
  },

  /** Current user's effective punch-delete permission */
  async getMyDeletePermission(): Promise<{
    mode: string;
    can_delete_all: boolean;
    allowed_target_ids: string[];
  }> {
    const res = await api.get('/attendance/my-delete-permission');
    return res.data.data;
  },
};
